import { Box, IconButton, Typography, Button, Stack, Grid2 as Grid } from '@mui/material';
import { useFieldArray, useWatch, useFormContext } from 'react-hook-form';
import { RiDeleteBin6Line } from 'react-icons/ri';
import FormSchemaParser from '@/components/Common/FormSchemaParser';
import Input from '@/components/Common/Input';
import Select from '@/components/Common/Select';

interface NexHealthFormProps {
  namePrefix: string;
  control: any;
  getValues?: any;
  setValue?: any;
  disabled?: boolean;
  configFields: any;
  spacing: string;
  submitForm?: any;
}

const emptyAppointmentType = () => ({
  id: '',
  name: '',
  minutes: '',
  emr_appt_descriptor_ids: []
});

const NexHealthForm = ({ namePrefix, control, disabled, configFields, spacing, submitForm }: NexHealthFormProps) => {
  const appointmentTypesField = configFields?.find((field: any) => field.key === 'appointment_types');
  const otherFields = configFields?.filter((field: any) => field.key !== 'appointment_types') || [];
  const descriptorField = appointmentTypesField?.group?.find((field: any) => field.key === 'emr_appt_descriptor_ids');

  const {
    fields: appointmentTypes,
    append,
    remove
  } = useFieldArray({
    control,
    name: `${namePrefix}.appointment_types`,
    keyName: 'fieldKey'
  });

  useWatch({
    control,
    name: `${namePrefix}.appointment_types`
  });

  return (
    <>
      {otherFields.length > 0 && (
        <FormSchemaParser
          disabled={disabled}
          fields={otherFields}
          control={control}
          namePrefix={namePrefix}
          gridSize={12}
          spacing={spacing}
          submitForm={(val: any) => submitForm?.(val)}
        />
      )}

      {appointmentTypesField && (
        <Box mt={2}>
          <Typography fontSize={16} fontWeight={600} my={2}>
            {appointmentTypesField.label || 'Appointment Types'}
          </Typography>

          {appointmentTypes.map((appointmentType: any, index: number) => (
            <AppointmentTypeItem
              key={appointmentType.fieldKey}
              index={index}
              control={control}
              remove={remove}
              disabled={disabled}
              namePrefix={namePrefix}
              descriptorOptions={descriptorField?.options || []}
            />
          ))}

          <Box display='flex' justifyContent='center' mt={2}>
            <Button variant='contained' disabled={disabled} onClick={() => append(emptyAppointmentType())}>
              + Add Appointment Type
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
};

export default NexHealthForm;

const AppointmentTypeItem = ({
  index,
  control,
  remove,
  disabled,
  namePrefix,
  descriptorOptions
}: {
  index: number;
  control: any;
  remove: (index: number) => void;
  disabled?: boolean;
  namePrefix: string;
  descriptorOptions: { value: any; label: any }[];
}) => {
  const itemPrefix = `${namePrefix}.appointment_types.${index}`;
  const { register } = useFormContext();

  return (
    <Box
      sx={{
        border: '1px solid #F3F3F3',
        borderRadius: 2,
        p: 2,
        mb: 2
      }}
    >
      <Stack direction='row' justifyContent='space-between' alignItems='center' mb={1}>
        <Typography fontSize={14} fontWeight={600}>
          Appointment Type {index + 1}
        </Typography>
        {!disabled && (
          <IconButton color='error' onClick={() => remove(index)}>
            <RiDeleteBin6Line />
          </IconButton>
        )}
      </Stack>

      <Grid container spacing={2}>
        <input type='hidden' {...register(`${itemPrefix}.id`)} />
        <Grid size={{ xs: 12, md: 6 }}>
          <Input name={`${itemPrefix}.name`} label='Name' control={control} fullWidth required disabled={disabled} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Input
            name={`${itemPrefix}.minutes`}
            label='Duration (minutes)'
            control={control}
            fullWidth
            required
            disabled={disabled}
          />
        </Grid>
        <Grid size={12}>
          <Select
            name={`${itemPrefix}.emr_appt_descriptor_ids`}
            label='EMR descriptors'
            control={control}
            options={descriptorOptions}
            multiple
            disabled={disabled}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
